<template>
  <div class="ur-landowner">
    <!-- 工具列 -->
    <div class="ur-lo-toolbar">
      <div class="ur-lo-toolbar-left"></div>
      <div class="ur-lo-toolbar-right">
        <button class="ur-lo-btn" @click="triggerImport">
          {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT') }}
        </button>
        <button class="ur-lo-btn" @click="exportData">
          {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.EXPORT') }}
        </button>
        <button
          v-if="selected.length > 0"
          class="ur-lo-btn ur-lo-btn-danger"
          @click="openDelete"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
          {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.DELETE_N', { count: selected.length }) }}
        </button>
      </div>
      <!-- 隱藏 file input -->
      <input
        type="file"
        accept=".xlsx"
        :key="fileInputKey"
        style="display:none"
        ref="fileInput"
        @change="onFileChange"
      />
    </div>

    <!-- 錯誤提示條 -->
    <div v-if="errorMsg" class="ur-lo-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-lo-error-close" @click="errorMsg = ''">&#10007;</button>
    </div>

    <!-- 讀取中 -->
    <div v-if="loading" class="ur-lo-loading">
      {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.LOADING') || '載入中...' }}
    </div>

    <!-- 表格 -->
    <div v-else class="ur-table-wrap">
      <table class="ur-table">
        <thead>
          <tr>
            <th class="ur-th-check">
              <input
                type="checkbox"
                :checked="allChecked"
                :indeterminate.prop="selected.length > 0 && !allChecked"
                @change="toggleAll"
              />
            </th>
            <th>{{ $t('URBANRENEWAL_ADMIN.PARKING.CODE') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.PARKING.SPEC') }}</th>
            <th>{{ $t('URBANRENEWAL_ADMIN.PARKING.TYPE') }}</th>
            <th class="ur-th-num">{{ $t('URBANRENEWAL_ADMIN.PARKING.VALUE') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pagedRows"
            :key="row.id"
            :class="{ 'ur-tr-selected': selected.includes(row.id) }"
          >
            <!-- Checkbox -->
            <td class="ur-td-check">
              <input
                type="checkbox"
                :checked="selected.includes(row.id)"
                @change="toggleRow(row.id)"
              />
            </td>
            <!-- 車位編號 -->
            <td>
              <UrbanRenewalInlineEdit
                :value="row.unit_code"
                type="text"
                @save="onCellSave(row, 'unit_code', $event)"
              />
            </td>
            <!-- 規格 -->
            <td>
              <UrbanRenewalInlineEdit
                :value="row.spec"
                type="text"
                @save="onCellSave(row, 'spec', $event)"
              />
            </td>
            <!-- 型式 -->
            <td>
              <UrbanRenewalInlineEdit
                :value="row.parking_type"
                type="text"
                @save="onCellSave(row, 'parking_type', $event)"
              />
            </td>
            <!-- 價值 -->
            <td class="ur-td-num">
              <UrbanRenewalInlineEdit
                :value="row.value"
                type="int"
                :display="fmt(row.value)"
                @save="onCellSave(row, 'value', $event)"
              />
            </td>
          </tr>
          <tr v-if="pagedRows.length === 0 && !loading">
            <td colspan="5" class="ur-td-empty">
              {{ projectId ? $t('URBANRENEWAL_ADMIN.LANDOWNER.NO_DATA') || '無資料' : $t('URBANRENEWAL_ADMIN.LANDOWNER.SELECT_PROJECT') || '請先選擇建案' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 分頁列（共用元件，設計稿樣式）-->
    <UrbanRenewalPagination
      :total="rows.length"
      :page.sync="page"
      :rpp.sync="rpp"
    />

    <!-- 刪除確認 dialog -->
    <v-dialog v-model="deleteDialog.visible" max-width="420">
      <v-card class="ur-dialog-card">
        <div class="ur-dialog-body">
          <p class="ur-dialog-msg">
            {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.DELETE_CONFIRM_ROWS', { count: selected.length }) }}
          </p>
        </div>
        <div class="ur-dialog-actions">
          <button class="ur-dialog-cancel-btn" @click="deleteDialog.visible = false">
            {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.CANCEL') }}
          </button>
          <button class="ur-dialog-confirm-btn" @click="confirmDelete">
            {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.CONFIRM') }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- 匯入結果 dialog（成功 / 部分 / 錯誤 統一呈現） -->
    <v-dialog v-model="importResultDialog.visible" max-width="600">
      <v-card class="ur-dialog-card">
        <div class="ur-dialog-body">
          <p class="ur-dialog-title">{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT_ERROR_TITLE') }}</p>

          <!-- 結構/檔案錯誤（400）：整份未匯入 -->
          <p v-if="importResultDialog.errorDetail" class="ur-dialog-msg ur-import-error-detail">
            {{ importResultDialog.errorDetail }}
          </p>

          <!-- 有結果（200） -->
          <template v-else>
            <!-- 全部成功、無跳過 -->
            <p v-if="importResultDialog.skipped.length === 0" class="ur-dialog-msg ur-import-success">
              {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT_SUCCESS', {
                created: importResultDialog.imported ? importResultDialog.imported.created || 0 : 0,
                updated: importResultDialog.imported ? importResultDialog.imported.updated || 0 : 0
              }) }}
            </p>
            <!-- 部分匯入：摘要 + 錯誤表 -->
            <template v-else>
              <p class="ur-dialog-msg">
                {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT_SUMMARY', {
                  created: importResultDialog.imported ? importResultDialog.imported.created || 0 : 0,
                  updated: importResultDialog.imported ? importResultDialog.imported.updated || 0 : 0
                }) }}
              </p>
              <div class="ur-import-error-table-wrap">
                <table class="ur-import-error-table">
                  <thead>
                    <tr>
                      <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.COL_ROW') }}</th>
                      <th>{{ $t('URBANRENEWAL_ADMIN.LANDOWNER.COL_REASON') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(e, idx) in importResultDialog.skipped" :key="idx">
                      <td>{{ e.row }}</td>
                      <td>{{ Array.isArray(e.reasons) ? e.reasons.join('、') : e.reasons }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </template>
        </div>
        <div class="ur-dialog-actions">
          <button class="ur-dialog-confirm-btn" @click="importResultDialog.visible = false">
            {{ $t('URBANRENEWAL_ADMIN.LANDOWNER.CLOSE') }}
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import UrbanRenewalDataService from '@/services/UrbanRenewalDataService';
import UrbanRenewalInlineEdit from '@/components/urbanrenewal/UrbanRenewalInlineEdit.vue';
import UrbanRenewalPagination from '@/components/urbanrenewal/UrbanRenewalPagination.vue';

export default {
  name: 'ParkingTab',

  components: { UrbanRenewalInlineEdit, UrbanRenewalPagination },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      rows: [],
      loading: false,
      selected: [],
      page: 1,
      rpp: 20,
      deleteDialog: { visible: false },
      importResultDialog: { visible: false, imported: null, skipped: [], errorDetail: '' },
      errorMsg: '',
      fileInputKey: 0,
    };
  },

  computed: {
    pagedRows() {
      const start = (this.page - 1) * this.rpp;
      return this.rows.slice(start, start + this.rpp);
    },

    pageIds() {
      return this.pagedRows.map((r) => r.id);
    },

    allChecked() {
      if (this.pagedRows.length === 0) return false;
      return this.pageIds.every((id) => this.selected.includes(id));
    },

    totalPages() {
      return Math.ceil(this.rows.length / this.rpp) || 1;
    },

    rppModel: {
      get() { return this.rpp; },
      set(val) { this.rpp = Number(val); this.page = 1; },
    },
  },

  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.loadRows();
      },
    },
    rows(v) {
      this.$emit('update:count', v.length);
    },
  },

  methods: {
    async loadRows() {
      if (!this.projectId) {
        this.rows = [];
        return;
      }
      this.loading = true;
      try {
        const res = await UrbanRenewalDataService.getParkingUnits(this.projectId);
        this.rows = res.data;
        this.selected = [];
        this.page = 1;
      } catch (err) {
        this.errorMsg = (err.response && err.response.data && err.response.data.detail) || '載入失敗';
      } finally {
        this.loading = false;
      }
    },

    async onCellSave(row, field, val) {
      try {
        await UrbanRenewalDataService.updateParkingUnit(this.projectId, row.id, { [field]: val });
        row[field] = val;
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.LANDOWNER.SAVE_FAILED');
      }
    },

    toggleRow(id) {
      const idx = this.selected.indexOf(id);
      if (idx === -1) this.selected.push(id);
      else this.selected.splice(idx, 1);
    },

    toggleAll() {
      if (this.allChecked) {
        this.selected = this.selected.filter((id) => !this.pageIds.includes(id));
      } else {
        this.pageIds.forEach((id) => {
          if (!this.selected.includes(id)) this.selected.push(id);
        });
      }
    },

    openDelete() {
      if (this.selected.length === 0) return;
      this.deleteDialog.visible = true;
    },

    async confirmDelete() {
      try {
        await UrbanRenewalDataService.deleteParkingUnits(this.projectId, this.selected);
        this.deleteDialog.visible = false;
        this.selected = [];
        await this.loadRows();
      } catch (err) {
        this.errorMsg = (err.response && err.response.data && err.response.data.detail) || '刪除失敗';
        this.deleteDialog.visible = false;
      }
    },

    triggerImport() {
      this.$refs.fileInput.click();
    },

    async onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await UrbanRenewalDataService.importParkingUnits(this.projectId, formData);
        const { imported, skipped } = res.data;
        await this.loadRows();
        this.importResultDialog = {
          visible: true,
          imported,
          skipped: skipped || [],
          errorDetail: '',
        };
      } catch (err) {
        const detail =
          (err.response && err.response.data && err.response.data.detail) ||
          this.$t('URBANRENEWAL_ADMIN.LANDOWNER.IMPORT_FAILED');
        this.importResultDialog = {
          visible: true,
          imported: null,
          skipped: [],
          errorDetail: detail,
        };
      } finally {
        this.fileInputKey++;
      }
    },

    async exportData() {
      try {
        const res = await UrbanRenewalDataService.exportParkingUnits(this.projectId);
        const blob = new Blob([res.data], { type: res.headers['content-type'] });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        let fn = '車位資料.xlsx';
        const cd = res.headers['content-disposition'];
        if (cd) {
          const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
          if (m) fn = decodeURIComponent(m[1]);
        }
        a.download = fn;
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.errorMsg = '匯出失敗';
      }
    },

    fmt(v) {
      if (v === null || v === undefined) return '';
      return Number(v).toLocaleString('zh-TW', { maximumFractionDigits: 0 });
    },
  },
};
</script>
